import { useEventLogging } from '@AppModels/Logging';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { ILocationState, InternalAccountIds } from '../../../models';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../models/hooks/appStateHooks';
import { SupportedIndustries } from '../../../models/MessageTemplates';
import { getIndustryName } from '../../../models/UiUtils';
import { useAutomationsOnHoldQuery } from '../../../queries/Automation/queries/useAutomationsOnHoldQuery';
import { useAutomationTemplatesQuery } from '../../../queries/Automation/queries/useAutomationTemplatesQuery';
import { useAvailableAutomationTriggersQuery } from '../../../queries/Automation/queries/useAvailableAutomationTriggersQuery';
import { useSystemAutomationTemplatesQuery } from '../../../queries/Automation/queries/useSystemAutomationTemplatesQuery';
import * as Api from '../../../viewmodels/AppViewModels';
import { Industry, ResourceSelectorId, TemplateStatus } from '../../../viewmodels/AppViewModels';
import { Button } from '../../components-v2/Button';
import {
	AppDrawer,
	AppDrawerToolbar,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../components-v2/Drawer/AppDrawer';
import { AutomationCard } from '../../components/automation/AutomationCard';
import { AutomationReportingCard } from '../../components/automation/AutomationReportingCard';
import { AutomationsPendingCard } from '../../components/automation/AutomationsPendingCard';
import { AutomationFilters } from '../../components/AutomationFilters';
import { LoadingLogo } from '../../components/LoadingLogo';
import { MultiContainerHeader } from '../../components/MultiContainerHeader';
import { DefaultSelectBox, ISelectBoxOption } from '../../components/SelectBox';
import { TabContent, TabList, TabRoot, TabTrigger } from '../../components/Tabs';
import { baseStyleSheet, bs } from '../../styles/styles';
import { DefaultUniversalAssistantChat } from '../templates/AssistantChat';
import { styleSheet } from './styles';
import { deriveResourceSelectorOptions, deriveTriggerTypeOptions } from './triggerTypeOptions';

interface IAutomationsProps {
	hideAppBarHeader?: boolean;
	hideOnHoldAutomations?: boolean;
	/** Impersonation context for admin viewing account automations */
	impersonationContext?: Api.IImpersonationContext;
	/** Return true if consumer will handle creation */
	onRequestCreate?(): boolean;
	/** Return true if consumer will handle editing */
	onRequestEdit?(automationTemplate: Api.IAutomationTemplateReference): boolean;
	styles?: StyleDeclarationValue[];
}

const IndustrySelectBoxOptions = SupportedIndustries.map<ISelectBoxOption<Industry>>(x => {
	return {
		title: getIndustryName(x),
		value: x as Industry,
	};
});

// Status options for inactive tab multi-select
const InactiveStatusOptions: { label: string; value: string }[] = [
	{ label: 'Draft', value: TemplateStatus.Draft },
	{ label: 'Disabled', value: TemplateStatus.Disabled },
	{ label: 'Archived', value: TemplateStatus.Archived },
];

const _Automations: React.FC<IAutomationsProps> = props => {
	const {
		hideAppBarHeader,
		hideOnHoldAutomations,
		impersonationContext: propsImpersonationContext,
		onRequestCreate,
		onRequestEdit,
		styles,
	} = props;

	// Hooks
	const history = useHistory();
	const location = useLocation();
	const match = useRouteMatch();
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const fullscreenModal = useFullscreenModal();
	const { logEvent, logInput, logApiError } = useEventLogging('Automations');

	// State
	const [industry, setIndustry] = useState<Industry | undefined>();
	const [searchText, setSearchText] = useState('');
	const [searchInput, setSearchInput] = useState('');
	const [selectedCreatorId, setSelectedCreatorId] = useState<string | null>(null);
	const [selectedTriggerType, setSelectedTriggerType] = useState<string>('all');
	const [activeTab, setActiveTab] = useState<'active' | 'inactive' | 'industry'>('active');
	const [selectedInactiveStatuses, setSelectedInactiveStatuses] = useState<TemplateStatus[]>([
		TemplateStatus.Draft,
		TemplateStatus.Disabled,
	]);
	const [selectedResourceSelectors, setSelectedResourceSelectors] = useState<string[]>([]);

	// Determine if this is the reporting view
	const isReporting = match.path.includes('reporting');

	// Get impersonation context - prefer prop (available on first render) over session (set in useEffect)
	const impersonationContext = propsImpersonationContext || userSession?.impersonationContext;

	// Query: Load automation templates by status - only fetch when respective tab is active
	const {
		data: publishedTemplatesData,
		isLoading: isLoadingPublished,
		error: publishedError,
	} = useAutomationTemplatesQuery({
		status: TemplateStatus.Published,
		forAnyTrigger: true,
		impersonationContext,
		enabled: activeTab === 'active',
	});

	const {
		data: disabledTemplatesData,
		isLoading: isLoadingDisabled,
		error: disabledError,
	} = useAutomationTemplatesQuery({
		status: TemplateStatus.Disabled,
		forAnyTrigger: true,
		impersonationContext,
		enabled: activeTab === 'inactive' && selectedInactiveStatuses.includes(TemplateStatus.Disabled),
	});

	const {
		data: draftTemplatesData,
		isLoading: isLoadingDraft,
		error: draftError,
	} = useAutomationTemplatesQuery({
		status: TemplateStatus.Draft,
		forAnyTrigger: true,
		impersonationContext,
		enabled: activeTab === 'inactive' && selectedInactiveStatuses.includes(TemplateStatus.Draft),
	});

	const {
		data: archivedTemplatesData,
		isLoading: isLoadingArchived,
		error: archivedError,
	} = useAutomationTemplatesQuery({
		status: TemplateStatus.Archived,
		forAnyTrigger: true,
		impersonationContext,
		enabled: activeTab === 'inactive' && selectedInactiveStatuses.includes(TemplateStatus.Archived),
	});

	// Loading state based on active tab
	const isLoadingActiveTab = activeTab === 'active' && isLoadingPublished;
	const isLoadingInactiveTab = activeTab === 'inactive' && (isLoadingDisabled || isLoadingDraft || isLoadingArchived);
	const allTemplatesError = publishedError || disabledError || draftError || archivedError;

	// Query: Load industry/system templates - only fetch when industry tab is active
	const { data: industryTemplatesData, isLoading: isLoadingIndustryTemplates } = useSystemAutomationTemplatesQuery({
		industry,
		enabled: true,
		impersonationContext,
	});

	// Query: Load available triggers for dynamic filter dropdowns
	const { data: availableTriggers } = useAvailableAutomationTriggersQuery({
		impersonationContext,
	});

	const triggerTypeOptions = useMemo(
		() => (availableTriggers ? deriveTriggerTypeOptions(availableTriggers) : undefined),
		[availableTriggers]
	);

	const resourceSelectorOptions = useMemo(
		() => (availableTriggers ? deriveResourceSelectorOptions(availableTriggers) : undefined),
		[availableTriggers]
	);

	// Query: Load automations on hold (conditional)
	const {
		data: automationsOnHoldData,
		isLoading: isLoadingOnHold,
		error: onHoldError,
	} = useAutomationsOnHoldQuery({
		enabled: !hideOnHoldAutomations && userSession?.account?.features?.automation?.enabled === true,
		impersonationContext,
	});

	// Combined loading state for logging
	const loading = isLoadingActiveTab || isLoadingInactiveTab || isLoadingIndustryTemplates;

	// Effect: Log initial load event
	useEffect(() => {
		if (!loading && !allTemplatesError) {
			logEvent('TemplatesLoad');
		}
	}, [loading, allTemplatesError, logEvent]);

	// Effect: Handle all templates error
	useEffect(() => {
		if (allTemplatesError) {
			logApiError('TemplatesLoad-Error', allTemplatesError as any);
			errorMessages.pushApiError(allTemplatesError as any);
		}
	}, [allTemplatesError, logApiError, errorMessages]);

	// Effect: Handle on-hold error
	useEffect(() => {
		if (onHoldError) {
			logApiError('LoadAutomationsOnHold-Error', onHoldError as any);
			errorMessages.pushApiError(onHoldError as any);
		}
	}, [onHoldError, logApiError, errorMessages]);

	// Effect: Navigate to pending automations if location.state is 'pending'
	useEffect(() => {
		if (location.state === 'pending' && !hideOnHoldAutomations && automationsOnHoldData && !isLoadingOnHold) {
			if (userSession?.account?.features?.automation?.enabled) {
				// For backward compatibility, pass the data in viewModel
				const locationState: ILocationState<any, any> = {
					viewModel: automationsOnHoldData as any,
				};

				fullscreenModal.history.push({
					pathname: `/automationsonhold/${ResourceSelectorId.AutomationsOnHold}`,
					state: locationState,
				});
			}
		}
	}, [location.state, hideOnHoldAutomations, automationsOnHoldData, isLoadingOnHold, userSession, fullscreenModal]);

	// Get templates for each tab directly from their respective queries (filter out nulls)
	const activeTemplates = useMemo(() => (publishedTemplatesData || []).filter(Boolean), [publishedTemplatesData]);

	const inactiveTemplates = useMemo(
		() =>
			[
				...(selectedInactiveStatuses.includes(TemplateStatus.Draft) ? draftTemplatesData || [] : []),
				...(selectedInactiveStatuses.includes(TemplateStatus.Disabled) ? disabledTemplatesData || [] : []),
				...(selectedInactiveStatuses.includes(TemplateStatus.Archived) ? archivedTemplatesData || [] : []),
			].filter(Boolean),
		[draftTemplatesData, disabledTemplatesData, archivedTemplatesData, selectedInactiveStatuses]
	);

	const industryTemplates = useMemo(() => (industryTemplatesData || []).filter(Boolean), [industryTemplatesData]);

	// Effect: Search debouncing
	useEffect(() => {
		const timer = setTimeout(() => {
			setSearchText(searchInput);
		}, 300);

		return () => clearTimeout(timer);
	}, [searchInput]);

	// Filtering function
	const getFilteredTemplates = useCallback(
		(templates: Api.IAutomationTemplateReference[]) => {
			return templates.filter(template => {
				if (!template) return false;
				// Search text filter - case insensitive search on automation name
				const searchMatch =
					!searchText || (template.name && template.name.toLowerCase().includes(searchText.toLowerCase()));

				// Creator filter - match by creator ID
				const creatorMatch = !selectedCreatorId || template.creator?.id === selectedCreatorId;

				// Trigger type filter - match by trigger type from published or draft version
				// Manual trigger means no publishedTrigger exists
				const triggerMatch =
					!selectedTriggerType ||
					selectedTriggerType === 'all' ||
					(selectedTriggerType === 'ManualAutomationTrigger'
						? !template.publishedTrigger
						: template.publishedTrigger?._type === selectedTriggerType);

				// Resource selector filter - only applies when trigger type is ResourceSelectorAutomationTrigger
				const resourceSelector = template.publishedTrigger?.resourceSelector as string | undefined;
				const resourceSelectorMatch =
					selectedTriggerType !== 'ResourceSelectorAutomationTrigger' ||
					selectedResourceSelectors.length === 0 ||
					(resourceSelector != null && selectedResourceSelectors.includes(resourceSelector));

				return searchMatch && creatorMatch && triggerMatch && resourceSelectorMatch;
			});
		},
		[searchText, selectedCreatorId, selectedTriggerType, selectedResourceSelectors]
	);

	// Apply filtering to each category
	const filteredActiveTemplates = useMemo(
		() => getFilteredTemplates(activeTemplates as any),
		[activeTemplates, getFilteredTemplates]
	);

	const filteredInactiveTemplates = useMemo(
		() => getFilteredTemplates(inactiveTemplates as any),
		[inactiveTemplates, getFilteredTemplates]
	);

	// Compute creator options from all templates
	const creatorOptions = useMemo(() => {
		const creators = new Map<string, string>();
		const options: { title: string; value: string | null }[] = [{ title: 'Show all creators', value: null }];

		// Combine all templates from all categories
		const allTemplates = [...activeTemplates, ...inactiveTemplates, ...industryTemplates];

		// Extract unique creators
		allTemplates.forEach(template => {
			if (template && template.creator?.id && !creators.has(template.creator.id)) {
				const creatorName =
					template.creator.firstName && template.creator.lastName
						? `${template.creator.firstName} ${template.creator.lastName}`.trim()
						: template.creator.email || 'Unknown Creator';

				creators.set(template.creator.id, creatorName);
				options.push({
					title: creatorName,
					value: template.creator.id,
				});
			}
		});

		// Sort creator options alphabetically (excluding "Show all creators")
		const sortedCreatorOptions = options.slice(1).sort((a, b) => {
			const titleA = typeof a.title === 'string' ? a.title : '';
			const titleB = typeof b.title === 'string' ? b.title : '';
			return titleA.localeCompare(titleB);
		});

		return [options[0], ...sortedCreatorOptions];
	}, [activeTemplates, inactiveTemplates, industryTemplates]);

	// Helper: Compute if a template can be edited
	const canEditTemplate = useCallback(
		(template: Api.IAutomationTemplateReference) => {
			const isAdmin =
				userSession.user.role === Api.EUserRole.SuperAdmin || userSession.user.role === Api.EUserRole.Admin;
			return template.status !== Api.TemplateStatus.Archived && (isAdmin || template.scope === Api.TemplateScope.User);
		},
		[userSession.user.role]
	);

	// Check if user is impersonating
	const isImpersonating = impersonationContext != null;

	// Render helper: Get automation templates by category
	const getAutomationTemplates = useCallback(
		(templates: 'active' | 'inactive' | 'industry') => {
			const filteredTemplates =
				templates === 'active'
					? filteredActiveTemplates
					: templates === 'inactive'
						? filteredInactiveTemplates
						: industryTemplates;

			return (
				<>
					{filteredTemplates.map((template: Api.IAutomationTemplateReference) => {
						return !isReporting ? (
							<AutomationCard
								automationTemplate={template}
								canEdit={canEditTemplate(template)}
								isImpersonating={isImpersonating}
								impersonationContext={impersonationContext}
								key={template.id}
								onRequestEdit={onRequestEdit ? () => onRequestEdit(template) || false : undefined}
							/>
						) : (
							<AutomationReportingCard automationTemplate={template} key={template.id} />
						);
					})}
				</>
			);
		},
		[
			filteredActiveTemplates,
			filteredInactiveTemplates,
			industryTemplates,
			isReporting,
			canEditTemplate,
			isImpersonating,
			impersonationContext,
			onRequestEdit,
		]
	);

	// Event handlers
	const handleSearchTextChange = useCallback((newSearchInput: string) => {
		setSearchInput(newSearchInput);
	}, []);

	const handleCreatorFilterChange = useCallback((creatorId: string | null) => {
		setSelectedCreatorId(creatorId);
	}, []);

	const handleTriggerTypeChange = useCallback((triggerType: string) => {
		setSelectedTriggerType(triggerType);
	}, []);

	const handleInactiveStatusChange = useCallback((statusStr: string) => {
		const status = statusStr as TemplateStatus;
		setSelectedInactiveStatuses(prev => {
			if (prev.includes(status)) {
				// Don't allow deselecting if it's the last one
				if (prev.length === 1) return prev;
				return prev.filter(s => s !== status);
			} else {
				return [...prev, status];
			}
		});
	}, []);

	const handleResourceSelectorChange = useCallback((resourceSelector: string) => {
		setSelectedResourceSelectors(prev =>
			prev.includes(resourceSelector) ? prev.filter(s => s !== resourceSelector) : [...prev, resourceSelector]
		);
	}, []);

	const handleTabChange = useCallback((tab: string) => {
		if (tab === 'active' || tab === 'inactive' || tab === 'industry') {
			setActiveTab(tab);
		}
	}, []);

	const canSeeIndustryTemplates =
		(industryTemplates.length > 0 || !!InternalAccountIds.has(userSession?.account?.id)) && !isReporting;

	const onIndustryChanged = useCallback(
		(option: ISelectBoxOption<Industry>) => {
			setIndustry(option.value);
			logEvent('LoadIndustryTemplates', { industry: option.value });
		},
		[logEvent]
	);

	const onCreateButtonClicked = useCallback(() => {
		if (!onRequestCreate?.()) {
			logInput('CreateAutomation', 'Click');
			history?.push({
				pathname: `${match.url}/new`,
			});
		}
	}, [onRequestCreate, logInput, history, match.url]);

	// Render helpers
	const renderIndustrySelector = () => {
		if (!InternalAccountIds.has(userSession?.account?.id)) {
			return null;
		}

		const selectedOption =
			IndustrySelectBoxOptions.find(
				x => x.value === (industry ? industry : userSession.account?.toJs().additionalInfo.industry)
			) || IndustrySelectBoxOptions.find(x => x.value === Industry.Miscellaneous);

		return (
			<DefaultSelectBox
				className={css(styleSheet.levTemplatesHeaderIndustrySelector)}
				onSelectionChanged={onIndustryChanged}
				options={IndustrySelectBoxOptions}
				optionStyles={[styleSheet.levTemplatesHeaderIndustrySelectorOption]}
				selectedOption={selectedOption}
				triggerStyles={[styleSheet.levTemplatesHeaderIndustrySelectorOption]}
			/>
		);
	};

	const renderHeaders = useCallback(() => {
		return (
			<>
				{!hideAppBarHeader && (
					<MultiContainerHeader appBarHeader={<h1 className={css(baseStyleSheet.breadcrumbTitle)}>Automations</h1>} />
				)}
				<AutomationsPendingCard shouldHide={isLoadingOnHold || hideOnHoldAutomations} />
			</>
		);
	}, [isLoadingOnHold, hideOnHoldAutomations, hideAppBarHeader]);

	// Main render
	return (
		<div className={css(styleSheet.container, ...(styles || []))}>
			{!isReporting ? renderHeaders() : null}
			<div className={css(styleSheet.body)}>
				{isLoadingIndustryTemplates ? (
					<div className={css(bs.flex, bs.justifyCenter, bs.itemsCenter)}>
						<LoadingLogo size={100} />
					</div>
				) : (
					<TabRoot value={activeTab} onValueChange={handleTabChange}>
						<TabList>
							<TabTrigger value='active'>Active</TabTrigger>
							<TabTrigger value='inactive'>Inactive</TabTrigger>
							{canSeeIndustryTemplates && <TabTrigger value='industry'>Sample</TabTrigger>}
						</TabList>

						{!isReporting && activeTab !== 'industry' && (
							<div className={css(styleSheet.filterRow)}>
								<AutomationFilters
									searchText={searchInput}
									selectedCreatorId={selectedCreatorId}
									creatorOptions={creatorOptions}
									selectedTriggerType={selectedTriggerType}
									onSearchTextChange={handleSearchTextChange}
									onCreatorFilterChange={handleCreatorFilterChange}
									onTriggerTypeChange={handleTriggerTypeChange}
									statusOptions={activeTab === 'inactive' ? InactiveStatusOptions : undefined}
									selectedStatuses={activeTab === 'inactive' ? (selectedInactiveStatuses as string[]) : undefined}
									onStatusChange={activeTab === 'inactive' ? handleInactiveStatusChange : undefined}
									selectedResourceSelectors={selectedResourceSelectors}
									onResourceSelectorChange={handleResourceSelectorChange}
									triggerTypeOptions={triggerTypeOptions}
									resourceSelectorOptions={resourceSelectorOptions}
								/>
								<Button size='lg' onClick={onCreateButtonClicked}>
									Create Automation
								</Button>
							</div>
						)}
						<TabContent value='active'>
							{isLoadingActiveTab ? (
								<div className={css(bs.flex, bs.justifyCenter, bs.itemsCenter, styleSheet.loadingContainer)}>
									<LoadingLogo size={100} />
								</div>
							) : (
								<div className={css(styleSheet.cardGrid)}>
									<div className={css(styleSheet.cardGridContent)}>{getAutomationTemplates('active')}</div>
								</div>
							)}
						</TabContent>
						<TabContent value='inactive'>
							{isLoadingInactiveTab ? (
								<div className={css(bs.flex, bs.justifyCenter, bs.itemsCenter, styleSheet.loadingContainer)}>
									<LoadingLogo size={100} />
								</div>
							) : (
								<div className={css(styleSheet.cardGrid)}>
									<div className={css(styleSheet.cardGridContent)}>{getAutomationTemplates('inactive')}</div>
								</div>
							)}
						</TabContent>
						<TabContent value='industry'>
							<div className={css(styleSheet.filterRow)}>{renderIndustrySelector()}</div>
							{isLoadingIndustryTemplates ? (
								<div className={css(bs.flex, bs.justifyCenter, bs.itemsCenter, styleSheet.loadingContainer)}>
									<LoadingLogo size={100} />
								</div>
							) : (
								<div className={css(styleSheet.cardGrid)}>
									<div className={css(styleSheet.cardGridContent)}>{getAutomationTemplates('industry')}</div>
								</div>
							)}
						</TabContent>
					</TabRoot>
				)}
			</div>
			<AppDrawer>
				<StickyAppDrawerContent value='ai-assistant'>
					<DefaultUniversalAssistantChat />
				</StickyAppDrawerContent>
				<AppDrawerToolbar>
					<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
				</AppDrawerToolbar>
			</AppDrawer>
		</div>
	);
};

export const Automations = _Automations;
